/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-09
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SuccessType } from '../../components/common/SuccessAlert';

// Success notification interface
export interface SuccessNotification {
  id: string;
  message: string;
  successType: SuccessType;
  showAction?: boolean;
  actionLabel?: string;
  autoHide?: boolean;
  hideDelay?: number;
}

// Notifications state interface
interface NotificationsState {
  successMessages: SuccessNotification[];
}

// Initial state
const initialState: NotificationsState = {
  successMessages: [],
};

// Generate unique ID for notifications
const generateId = (): string =>
  `notification_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

/**
 * Redux slice for managing success notifications
 */
const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /**
     * Add a new success notification to the queue
     */
    showSuccessNotification: (
      state,
      action: PayloadAction<Omit<SuccessNotification, 'id'>>
    ) => {
      const notification: SuccessNotification = {
        id: generateId(),
        ...action.payload,
      };
      // eslint-disable-next-line no-param-reassign
      state.successMessages.push(notification);
    },

    /**
     * Remove a specific success notification by ID
     */
    hideSuccessNotification: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign
      state.successMessages = state.successMessages.filter(
        notification => notification.id !== action.payload
      );
    },

    /**
     * Clear all success notifications
     */
    clearAllSuccessNotifications: state => {
      // eslint-disable-next-line no-param-reassign
      state.successMessages = [];
    },
  },
});

// Export actions
export const {
  showSuccessNotification,
  hideSuccessNotification,
  clearAllSuccessNotifications,
} = notificationsSlice.actions;

// Export reducer
export const notificationsReducer = notificationsSlice.reducer;

// Export selectors
export const selectSuccessNotifications = (state: {
  notifications: NotificationsState;
}) => state.notifications.successMessages;

export const selectLatestSuccessNotification = (state: {
  notifications: NotificationsState;
}) =>
  state.notifications.successMessages[
    state.notifications.successMessages.length - 1
  ] || null;
